import { useStore } from '@/components/common/store';
import type { GeoInventoryResponse } from '@/interfaces/api/v1-geo-inventory';
import type { AxiosError } from 'axios';
import axios from 'axios';
import type { DestinationElement } from '../../common/types';
import * as destinationService from '@components/common/services/destinationService';
import type { Ref } from 'vue';
import type { HotelListData } from '@/interfaces/components/hotelListData';

const store = useStore();

const apiData = store.getters['searchMask/apiData'];
const geoInventoryUrl = 'v1/geo-inventory';

function isGeoInventoryResponse(arg: [] | GeoInventoryResponse): arg is GeoInventoryResponse {
	return !Array.isArray(arg);
}

const updateSelectedGeoInventory = (
	hotelListData: Ref<HotelListData>,
	destinations: DestinationElement[],
	locationIdName: Ref<'RegionID' | 'CityID'>
) => {
	let actualDestinationId = store.state.searchMask?.destinationTypes?.[0];
	if (hotelListData.value.locationType === 'CITY' && hotelListData.value.cyid) {
		actualDestinationId = +hotelListData.value.cyid;
	}
	let availableHotelCount = 0;
	if (Array.isArray(destinations)) {
		destinations.forEach((destination) => {
			if (actualDestinationId && destination[locationIdName.value] === actualDestinationId) {
				store.commit('searchMask/updateFormData', { selectedGeoInventory: destination });
			}
			availableHotelCount += destination.Count;
		});
	}
	store.commit('searchMask/updateAvailableHotelCount', availableHotelCount);
};

export const fetchGeoInventory = async (
	hotelListData: Ref<HotelListData>,
	isOnlyHotel: boolean,
	locationIdName: Ref<'RegionID' | 'CityID'>
): Promise<void> => {
	const listType = hotelListData.value.locationType;
	const travelType = isOnlyHotel ? 'Accommodation' : 'Package';
	const { exclude, bodyParams } = destinationService.createExcludeAndBodyParams(hotelListData.value, travelType);

	const body = apiData(bodyParams, exclude);
	const uri = geoInventoryUrl;

	try {
		const response = await destinationService.fetchDestinations(body, uri);
		if (!response) {
			return;
		}
		if (isGeoInventoryResponse(response)) {
			if ((listType === 'COUNTRY' || listType === 'REGIONGROUP') && response.Regions) {
				store.dispatch('updateTypes', {
					destination: response.Regions,
				});
				updateSelectedGeoInventory(hotelListData, response.Regions, locationIdName);
			}
			if ((listType === 'TOPREGION' || listType === 'CITY') && response.Cities) {
				store.dispatch('updateTypes', {
					destination: response.Cities,
				});
				updateSelectedGeoInventory(hotelListData, response.Cities, locationIdName);
			}
		} else {
			store.dispatch('updateTypes', {
				destination: response,
			});
			updateSelectedGeoInventory(hotelListData, response, locationIdName);
		}
	} catch (axiosError) {
		if (!axios.isCancel(axiosError as AxiosError)) {
			console.error('GeoInventory: ', axiosError);
		}
	} finally {
		store.dispatch('updateDestinationFilterLoading', false);
	}
};
